/**
 * Procedural texture atlas — 16×16 pixel-art textures for each block type.
 * Atlas: 256×256 canvas (16×16 grid of 16×16 tiles).
 */

import * as THREE from "three";
import { BlockType } from "@craftgaming/shared";

const TILE = 16;
const ATLAS = 256; // 16 tiles × 16px
const COLS = ATLAS / TILE;

// Texture tile positions: [col, row]
const TEX = {
  DIRT: [0, 0] as const,
  GRASS_TOP: [1, 0] as const,
  GRASS_SIDE: [2, 0] as const,
  STONE: [3, 0] as const,
  SAND: [4, 0] as const,
  WOOD_TOP: [5, 0] as const,
  WOOD_SIDE: [6, 0] as const,
  LEAVES: [7, 0] as const,
  COBBLESTONE: [8, 0] as const,
  PLANKS: [9, 0] as const,
  GLASS: [10, 0] as const,
  BEDROCK: [11, 0] as const,
  COAL_ORE: [12, 0] as const,
  IRON_ORE: [13, 0] as const,
  GOLD_ORE: [14, 0] as const,
  DIAMOND_ORE: [15, 0] as const,
  WATER: [0, 1] as const,
  CRAFTING_TABLE_TOP: [1, 1] as const,
  CRAFTING_TABLE_SIDE: [2, 1] as const,
  BED_TOP: [3, 1] as const,
  BED_SIDE: [4, 1] as const,
  CHEST_TOP: [5, 1] as const,
  CHEST_SIDE: [6, 1] as const,
  FURNACE_TOP: [7, 1] as const,
  FURNACE_FRONT: [8, 1] as const,
};

type FaceType = "top" | "bottom" | "side";
type TileCoord = readonly [number, number];

// Block → texture per face
const BLOCK_FACES: Record<number, { top: TileCoord; side: TileCoord; bottom: TileCoord }> = {
  [BlockType.DIRT]: { top: TEX.DIRT, side: TEX.DIRT, bottom: TEX.DIRT },
  [BlockType.GRASS]: { top: TEX.GRASS_TOP, side: TEX.GRASS_SIDE, bottom: TEX.DIRT },
  [BlockType.STONE]: { top: TEX.STONE, side: TEX.STONE, bottom: TEX.STONE },
  [BlockType.SAND]: { top: TEX.SAND, side: TEX.SAND, bottom: TEX.SAND },
  [BlockType.WOOD]: { top: TEX.WOOD_TOP, side: TEX.WOOD_SIDE, bottom: TEX.WOOD_TOP },
  [BlockType.LEAVES]: { top: TEX.LEAVES, side: TEX.LEAVES, bottom: TEX.LEAVES },
  [BlockType.COBBLESTONE]: { top: TEX.COBBLESTONE, side: TEX.COBBLESTONE, bottom: TEX.COBBLESTONE },
  [BlockType.PLANKS]: { top: TEX.PLANKS, side: TEX.PLANKS, bottom: TEX.PLANKS },
  [BlockType.GLASS]: { top: TEX.GLASS, side: TEX.GLASS, bottom: TEX.GLASS },
  [BlockType.BEDROCK]: { top: TEX.BEDROCK, side: TEX.BEDROCK, bottom: TEX.BEDROCK },
  [BlockType.COAL_ORE]: { top: TEX.COAL_ORE, side: TEX.COAL_ORE, bottom: TEX.COAL_ORE },
  [BlockType.IRON_ORE]: { top: TEX.IRON_ORE, side: TEX.IRON_ORE, bottom: TEX.IRON_ORE },
  [BlockType.GOLD_ORE]: { top: TEX.GOLD_ORE, side: TEX.GOLD_ORE, bottom: TEX.GOLD_ORE },
  [BlockType.DIAMOND_ORE]: { top: TEX.DIAMOND_ORE, side: TEX.DIAMOND_ORE, bottom: TEX.DIAMOND_ORE },
  [BlockType.WATER]: { top: TEX.WATER, side: TEX.WATER, bottom: TEX.WATER },
  [BlockType.CRAFTING_TABLE]: { top: TEX.CRAFTING_TABLE_TOP, side: TEX.CRAFTING_TABLE_SIDE, bottom: TEX.PLANKS },
  [BlockType.BED]: { top: TEX.BED_TOP, side: TEX.BED_SIDE, bottom: TEX.PLANKS },
  [BlockType.CHEST]: { top: TEX.CHEST_TOP, side: TEX.CHEST_SIDE, bottom: TEX.CHEST_TOP },
  [BlockType.FURNACE]: { top: TEX.FURNACE_TOP, side: TEX.FURNACE_FRONT, bottom: TEX.STONE },
};

// Seeded pseudo-random for consistent patterns
function seededRng(seed: number) {
  let s = seed;
  return () => {
    s = (s * 16807 + 11) % 2147483647;
    return (s - 1) / 2147483646;
  };
}

// ─── Pixel drawing helpers ───

function fill(ctx: CanvasRenderingContext2D, ox: number, oy: number, color: string) {
  ctx.fillStyle = color;
  ctx.fillRect(ox, oy, TILE, TILE);
}

function pixel(ctx: CanvasRenderingContext2D, ox: number, oy: number, x: number, y: number, color: string) {
  ctx.fillStyle = color;
  ctx.fillRect(ox + x, oy + y, 1, 1);
}

function scatter(
  ctx: CanvasRenderingContext2D,
  ox: number, oy: number,
  color: string, count: number, rng: () => number
) {
  ctx.fillStyle = color;
  for (let i = 0; i < count; i++) {
    const x = Math.floor(rng() * TILE);
    const y = Math.floor(rng() * TILE);
    ctx.fillRect(ox + x, oy + y, 1, 1);
  }
}

// ─── Individual texture drawers ───

function drawDirt(ctx: CanvasRenderingContext2D, ox: number, oy: number) {
  fill(ctx, ox, oy, "#8B6914");
  const rng = seededRng(100);
  scatter(ctx, ox, oy, "#6B4F12", 35, rng);
  scatter(ctx, ox, oy, "#9B7924", 15, rng);
  scatter(ctx, ox, oy, "#7B5914", 10, rng);
}

function drawGrassTop(ctx: CanvasRenderingContext2D, ox: number, oy: number) {
  fill(ctx, ox, oy, "#4B8B20");
  const rng = seededRng(200);
  scatter(ctx, ox, oy, "#3B7B10", 30, rng);
  scatter(ctx, ox, oy, "#5B9B30", 20, rng);
  scatter(ctx, ox, oy, "#2B6B00", 10, rng);
  scatter(ctx, ox, oy, "#6BAB40", 8, rng);
}

function drawGrassSide(ctx: CanvasRenderingContext2D, ox: number, oy: number) {
  // Bottom part: dirt
  fill(ctx, ox, oy, "#8B6914");
  const rng = seededRng(101);
  scatter(ctx, ox, oy, "#6B4F12", 30, rng);
  scatter(ctx, ox, oy, "#9B7924", 12, rng);

  // Top 3-4 rows: grass
  ctx.fillStyle = "#4B8B20";
  ctx.fillRect(ox, oy, TILE, 3);

  // Irregular grass edge (row 3-5)
  const rng2 = seededRng(300);
  for (let x = 0; x < TILE; x++) {
    const depth = Math.floor(rng2() * 3);
    if (depth > 0) {
      ctx.fillStyle = "#4B8B20";
      ctx.fillRect(ox + x, oy + 3, 1, depth);
    }
  }

  // Darker grass pixels on top
  scatter(ctx, ox, oy, "#3B7B10", 6, seededRng(301));
}

function drawStone(ctx: CanvasRenderingContext2D, ox: number, oy: number) {
  fill(ctx, ox, oy, "#8A8A8A");
  const rng = seededRng(400);
  scatter(ctx, ox, oy, "#707070", 30, rng);
  scatter(ctx, ox, oy, "#9A9A9A", 20, rng);
  scatter(ctx, ox, oy, "#606060", 8, rng);

  // Crack lines
  ctx.fillStyle = "#555";
  for (let i = 0; i < 3; i++) {
    const sx = Math.floor(rng() * 12);
    const sy = Math.floor(rng() * 12);
    const len = 2 + Math.floor(rng() * 4);
    for (let j = 0; j < len; j++) {
      pixel(ctx, ox, oy, (sx + j) % TILE, (sy + Math.floor(rng() * 2)) % TILE, "#555");
    }
  }
}

function drawSand(ctx: CanvasRenderingContext2D, ox: number, oy: number) {
  fill(ctx, ox, oy, "#DBCD7E");
  const rng = seededRng(500);
  scatter(ctx, ox, oy, "#C8BA6E", 25, rng);
  scatter(ctx, ox, oy, "#E8DA8E", 20, rng);
  scatter(ctx, ox, oy, "#B8AA5E", 8, rng);
}

function drawWoodTop(ctx: CanvasRenderingContext2D, ox: number, oy: number) {
  fill(ctx, ox, oy, "#B8904A");
  // Ring pattern
  const cx = 8, cy = 8;
  for (let x = 0; x < TILE; x++) {
    for (let y = 0; y < TILE; y++) {
      const dist = Math.sqrt((x - cx) ** 2 + (y - cy) ** 2);
      const ring = Math.floor(dist) % 3;
      if (ring === 0) {
        pixel(ctx, ox, oy, x, y, "#A07838");
      } else if (ring === 2) {
        pixel(ctx, ox, oy, x, y, "#C8A058");
      }
    }
  }
  // Center dot
  pixel(ctx, ox, oy, 7, 7, "#705020");
  pixel(ctx, ox, oy, 8, 7, "#705020");
  pixel(ctx, ox, oy, 7, 8, "#705020");
  pixel(ctx, ox, oy, 8, 8, "#705020");
}

function drawWoodSide(ctx: CanvasRenderingContext2D, ox: number, oy: number) {
  fill(ctx, ox, oy, "#6B4D2A");
  const rng = seededRng(600);

  // Vertical bark lines
  for (let x = 0; x < TILE; x++) {
    if (x % 3 === 0 || x % 5 === 0) {
      for (let y = 0; y < TILE; y++) {
        pixel(ctx, ox, oy, x, y, "#5B3D1A");
      }
    }
  }
  scatter(ctx, ox, oy, "#7B5D3A", 15, rng);
  scatter(ctx, ox, oy, "#4B2D0A", 8, rng);
}

function drawLeaves(ctx: CanvasRenderingContext2D, ox: number, oy: number) {
  fill(ctx, ox, oy, "#2D8A2D");
  const rng = seededRng(700);
  scatter(ctx, ox, oy, "#1D6A1D", 30, rng);
  scatter(ctx, ox, oy, "#3DAA3D", 20, rng);
  scatter(ctx, ox, oy, "#0D5A0D", 12, rng);
  // Small gaps (transparency feel)
  scatter(ctx, ox, oy, "#1B5B1B", 8, rng);
}

function drawCobblestone(ctx: CanvasRenderingContext2D, ox: number, oy: number) {
  fill(ctx, ox, oy, "#6A6A6A");
  const rng = seededRng(800);

  // Draw stone shapes
  const stoneColors = ["#7A7A7A", "#8A8A8A", "#5A5A5A", "#757575"];
  for (let i = 0; i < 8; i++) {
    const sx = Math.floor(rng() * 12);
    const sy = Math.floor(rng() * 12);
    const sw = 2 + Math.floor(rng() * 3);
    const sh = 2 + Math.floor(rng() * 3);
    ctx.fillStyle = stoneColors[i % stoneColors.length];
    ctx.fillRect(ox + sx, oy + sy, sw, sh);
  }

  // Dark grout lines
  scatter(ctx, ox, oy, "#3A3A3A", 15, rng);
}

function drawPlanks(ctx: CanvasRenderingContext2D, ox: number, oy: number) {
  fill(ctx, ox, oy, "#BC9452");
  const rng = seededRng(900);

  // Horizontal plank lines
  for (const row of [0, 4, 8, 12]) {
    ctx.fillStyle = "#8A6A32";
    ctx.fillRect(ox, oy + row, TILE, 1);
  }

  // Grain lines
  for (let y = 0; y < TILE; y++) {
    if (y % 4 === 0) continue; // skip plank borders
    const rx = Math.floor(rng() * 14) + 1;
    pixel(ctx, ox, oy, rx, y, "#A88442");
  }

  scatter(ctx, ox, oy, "#CCA462", 10, rng);
  scatter(ctx, ox, oy, "#9A7A3A", 8, rng);
}

function drawGlass(ctx: CanvasRenderingContext2D, ox: number, oy: number) {
  fill(ctx, ox, oy, "#C8DDF0");

  // Frame border
  ctx.fillStyle = "#8AACCC";
  ctx.fillRect(ox, oy, TILE, 1);
  ctx.fillRect(ox, oy + 15, TILE, 1);
  ctx.fillRect(ox, oy, 1, TILE);
  ctx.fillRect(ox + 15, oy, 1, TILE);

  // Shine highlight
  pixel(ctx, ox, oy, 3, 3, "#E8F0FF");
  pixel(ctx, ox, oy, 4, 3, "#E8F0FF");
  pixel(ctx, ox, oy, 3, 4, "#E8F0FF");
  pixel(ctx, ox, oy, 4, 4, "#D8E8F8");
}

function drawBedrock(ctx: CanvasRenderingContext2D, ox: number, oy: number) {
  fill(ctx, ox, oy, "#333333");
  const rng = seededRng(1100);
  scatter(ctx, ox, oy, "#222222", 25, rng);
  scatter(ctx, ox, oy, "#444444", 20, rng);
  scatter(ctx, ox, oy, "#1A1A1A", 15, rng);
  scatter(ctx, ox, oy, "#555555", 5, rng);
}

function drawOre(ctx: CanvasRenderingContext2D, ox: number, oy: number, oreColor: string, seed: number) {
  // Stone base
  drawStone(ctx, ox, oy);
  // Ore spots
  const rng = seededRng(seed);
  for (let i = 0; i < 6; i++) {
    const sx = 2 + Math.floor(rng() * 10);
    const sy = 2 + Math.floor(rng() * 10);
    const size = 1 + Math.floor(rng() * 2);
    ctx.fillStyle = oreColor;
    ctx.fillRect(ox + sx, oy + sy, size, size);
  }
}

function drawWater(ctx: CanvasRenderingContext2D, ox: number, oy: number) {
  fill(ctx, ox, oy, "#3366AA");
  const rng = seededRng(1600);
  scatter(ctx, ox, oy, "#2255AA", 20, rng);
  scatter(ctx, ox, oy, "#4477BB", 15, rng);
  scatter(ctx, ox, oy, "#5588CC", 8, rng);
}

// ─── Atlas creation ───

function drawCraftingTableTop(ctx: CanvasRenderingContext2D, ox: number, oy: number) {
  fill(ctx, ox, oy, "#B38B4D");
  const rng = seededRng(1700);
  scatter(ctx, ox, oy, "#A37B3D", 20, rng);
  // Grid (work surface)
  ctx.fillStyle = "#6B4F22";
  ctx.fillRect(ox, oy + 7, TILE, 1);
  ctx.fillRect(ox + 7, oy, 1, TILE);
  ctx.fillStyle = "#8B6932";
  ctx.fillRect(ox, oy, TILE, 1);
  ctx.fillRect(ox, oy + 15, TILE, 1);
  ctx.fillRect(ox, oy, 1, TILE);
  ctx.fillRect(ox + 15, oy, 1, TILE);
}

function drawCraftingTableSide(ctx: CanvasRenderingContext2D, ox: number, oy: number) {
  fill(ctx, ox, oy, "#B38B4D");
  const rng = seededRng(1701);
  scatter(ctx, ox, oy, "#A37B3D", 18, rng);
  // Tools silhouette (saw + hammer)
  ctx.fillStyle = "#5B4322";
  ctx.fillRect(ox + 3, oy + 4, 4, 2);
  ctx.fillRect(ox + 4, oy + 6, 2, 5);
  ctx.fillStyle = "#777777";
  ctx.fillRect(ox + 9, oy + 3, 2, 6);
  ctx.fillRect(ox + 8, oy + 3, 4, 2);
}

function drawBedTop(ctx: CanvasRenderingContext2D, ox: number, oy: number) {
  // Blanket
  fill(ctx, ox, oy, "#C62828");
  const rng = seededRng(1800);
  scatter(ctx, ox, oy, "#B61818", 15, rng);
  // Pillow (head end)
  ctx.fillStyle = "#F0F0F0";
  ctx.fillRect(ox + 2, oy + 1, 12, 4);
  ctx.fillStyle = "#D8D8D8";
  ctx.fillRect(ox + 2, oy + 4, 12, 1);
  // Blanket fold
  ctx.fillStyle = "#E04040";
  ctx.fillRect(ox, oy + 6, TILE, 1);
}

function drawBedSide(ctx: CanvasRenderingContext2D, ox: number, oy: number) {
  // Wooden frame bottom, blanket top
  fill(ctx, ox, oy, "#8B5A1E");
  ctx.fillStyle = "#C62828";
  ctx.fillRect(ox, oy, TILE, 8);
  ctx.fillStyle = "#F0F0F0";
  ctx.fillRect(ox, oy, 5, 4);
}

function drawChestTop(ctx: CanvasRenderingContext2D, ox: number, oy: number) {
  fill(ctx, ox, oy, "#9A6B2F");
  const rng = seededRng(1900);
  scatter(ctx, ox, oy, "#8A5B24", 22, rng);
  // Frame
  ctx.fillStyle = "#5B4322";
  ctx.fillRect(ox, oy, TILE, 1);
  ctx.fillRect(ox, oy + 15, TILE, 1);
  ctx.fillRect(ox, oy, 1, TILE);
  ctx.fillRect(ox + 15, oy, 1, TILE);
}

function drawChestSide(ctx: CanvasRenderingContext2D, ox: number, oy: number) {
  fill(ctx, ox, oy, "#9A6B2F");
  const rng = seededRng(1901);
  scatter(ctx, ox, oy, "#8A5B24", 20, rng);
  // Frame + lid seam
  ctx.fillStyle = "#5B4322";
  ctx.fillRect(ox, oy, TILE, 1);
  ctx.fillRect(ox, oy + 15, TILE, 1);
  ctx.fillRect(ox, oy, 1, TILE);
  ctx.fillRect(ox + 15, oy, 1, TILE);
  ctx.fillRect(ox, oy + 5, TILE, 1);
  // Latch
  ctx.fillStyle = "#B0B0B0";
  ctx.fillRect(ox + 7, oy + 4, 2, 4);
}

function drawFurnaceTop(ctx: CanvasRenderingContext2D, ox: number, oy: number) {
  fill(ctx, ox, oy, "#8a8a8a");
  const rng = seededRng(2000);
  scatter(ctx, ox, oy, "#7a7a7a", 22, rng);
  scatter(ctx, ox, oy, "#9a9a9a", 14, rng);
  ctx.fillStyle = "#5a5a5a";
  ctx.fillRect(ox, oy, TILE, 1);
  ctx.fillRect(ox, oy + 15, TILE, 1);
  ctx.fillRect(ox, oy, 1, TILE);
  ctx.fillRect(ox + 15, oy, 1, TILE);
}

function drawFurnaceFront(ctx: CanvasRenderingContext2D, ox: number, oy: number) {
  fill(ctx, ox, oy, "#8a8a8a");
  const rng = seededRng(2001);
  scatter(ctx, ox, oy, "#7a7a7a", 20, rng);
  // Dark opening with glowing embers
  ctx.fillStyle = "#2a2a2a";
  ctx.fillRect(ox + 4, oy + 7, 8, 6);
  ctx.fillStyle = "#e05a10";
  ctx.fillRect(ox + 5, oy + 10, 6, 2);
  ctx.fillStyle = "#ffca28";
  ctx.fillRect(ox + 6, oy + 11, 2, 1);
  ctx.fillRect(ox + 9, oy + 11, 1, 1);
  // Frame
  ctx.fillStyle = "#5a5a5a";
  ctx.fillRect(ox, oy, TILE, 1);
  ctx.fillRect(ox, oy + 15, TILE, 1);
}

function generateAtlas(): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.width = ATLAS;
  canvas.height = ATLAS;
  const ctx = canvas.getContext("2d")!;

  // Clear to magenta (debug: missing textures visible)
  ctx.fillStyle = "#FF00FF";
  ctx.fillRect(0, 0, ATLAS, ATLAS);

  const at = (col: number, row: number): [number, number] => [col * TILE, row * TILE];

  drawDirt(ctx, ...at(...TEX.DIRT));
  drawGrassTop(ctx, ...at(...TEX.GRASS_TOP));
  drawGrassSide(ctx, ...at(...TEX.GRASS_SIDE));
  drawStone(ctx, ...at(...TEX.STONE));
  drawSand(ctx, ...at(...TEX.SAND));
  drawWoodTop(ctx, ...at(...TEX.WOOD_TOP));
  drawWoodSide(ctx, ...at(...TEX.WOOD_SIDE));
  drawLeaves(ctx, ...at(...TEX.LEAVES));
  drawCobblestone(ctx, ...at(...TEX.COBBLESTONE));
  drawPlanks(ctx, ...at(...TEX.PLANKS));
  drawGlass(ctx, ...at(...TEX.GLASS));
  drawBedrock(ctx, ...at(...TEX.BEDROCK));
  drawOre(ctx, ...at(...TEX.COAL_ORE), "#222222", 1200);
  drawOre(ctx, ...at(...TEX.IRON_ORE), "#CCAA88", 1300);
  drawOre(ctx, ...at(...TEX.GOLD_ORE), "#FFDD44", 1400);
  drawOre(ctx, ...at(...TEX.DIAMOND_ORE), "#44DDEE", 1500);
  drawWater(ctx, ...at(...TEX.WATER));
  drawCraftingTableTop(ctx, ...at(...TEX.CRAFTING_TABLE_TOP));
  drawCraftingTableSide(ctx, ...at(...TEX.CRAFTING_TABLE_SIDE));
  drawBedTop(ctx, ...at(...TEX.BED_TOP));
  drawBedSide(ctx, ...at(...TEX.BED_SIDE));
  drawChestTop(ctx, ...at(...TEX.CHEST_TOP));
  drawChestSide(ctx, ...at(...TEX.CHEST_SIDE));
  drawFurnaceTop(ctx, ...at(...TEX.FURNACE_TOP));
  drawFurnaceFront(ctx, ...at(...TEX.FURNACE_FRONT));

  return canvas;
}

// ─── Public API ───

export type TextureAtlas = {
  texture: THREE.CanvasTexture;
  getBlockFaceUVs: (blockId: number, faceType: FaceType, localUVs: number[][]) => number[];
  getBlockPreview: (blockId: number, size: number) => string;
};

let cached: TextureAtlas | null = null;

export function getTextureAtlas(): TextureAtlas {
  if (cached) return cached;

  const canvas = generateAtlas();
  const texture = new THREE.CanvasTexture(canvas);
  texture.magFilter = THREE.NearestFilter;
  texture.minFilter = THREE.NearestFilter;
  texture.colorSpace = THREE.SRGBColorSpace;

  function getBlockFaceUVs(blockId: number, faceType: FaceType, localUVs: number[][]): number[] {
    const faces = BLOCK_FACES[blockId];
    const tile = faces ? faces[faceType] : TEX.STONE;
    const [col, row] = tile;

    const uvs: number[] = [];
    for (const [lu, lv] of localUVs) {
      const u = (col + lu) / COLS;
      const v = 1 - (row + lv) / COLS;
      uvs.push(u, v);
    }
    return uvs;
  }

  // Preview cache: blockId → Map<size, dataURL>
  const previewCache = new Map<string, string>();

  function getBlockPreview(blockId: number, size: number): string {
    const cacheKey = `${blockId}_${size}`;
    const hit = previewCache.get(cacheKey);
    if (hit) return hit;

    const faceDef = BLOCK_FACES[blockId];
    const tile = faceDef ? faceDef.side : TEX.STONE;
    const [col, row] = tile;

    // Extract tile from atlas canvas
    const previewCanvas = document.createElement("canvas");
    previewCanvas.width = size;
    previewCanvas.height = size;
    const pctx = previewCanvas.getContext("2d")!;
    pctx.imageSmoothingEnabled = false;
    pctx.drawImage(canvas, col * TILE, row * TILE, TILE, TILE, 0, 0, size, size);

    const dataUrl = previewCanvas.toDataURL();
    previewCache.set(cacheKey, dataUrl);
    return dataUrl;
  }

  cached = { texture, getBlockFaceUVs, getBlockPreview };
  return cached;
}
